import React, { useEffect, useState, useContext, useRef } from "react";
import Navbar from "../components/common/Navbar";
import ReportsTable from "../components/documents-reports/ReportsTable";
import { AuthContext } from "../context/Auth/AuthContext";
import requests from "../api/requests";
import baseUrl, { sugarCasesUrl } from "../config/baseUrl";
import {
  CaretSortIcon,
  ChevronDownIcon,
  StarIcon,
  StarFilledIcon,
  EyeOpenIcon,
} from "@radix-ui/react-icons";

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from "@tanstack/react-table";

import { Button } from "../shadcn-components/ui/button";
import { Checkbox } from "../shadcn-components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../shadcn-components/ui/dropdown-menu";
import { Input } from "../shadcn-components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../shadcn-components/ui/table";

import "../styles/Starred.css";
import { Link } from "react-router-dom";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../shadcn-components/ui/tooltip";

import { useDebounce } from "use-debounce";
import { convertToUTC, formatDate } from "../utils/formatDate";
import { format } from "date-fns";

/*** Main component for listing the details of document requests (count of uploaded documents for requests) for clients ***/
const DocumentsReports = () => {
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [requestsDocumentsResponse, setRequestsDocumentsResponse] = useState(
    []
  );
  const { token, user_id, is_admin } = useContext(AuthContext);

  const [api, setApi] = useState(
    `${baseUrl}/api/get-doc-requested-data-of-client?limit=${paginationModel.pageSize}&offset=${paginationModel.page}&userId=${user_id}&isAdmin=${is_admin}`
  );

  //new states
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({
    contact_type: false,
  });
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);
  const [createdAt, setCreatedAt] = useState(null);
  const [lastRequestedDate, setLastRequestedDate] = useState(null);

  const prevFilterParamsRef = useRef({});
  const prevStateRef = useRef({
    createdAt,
    lastRequestedDate,
    debouncedGlobalFilter,
    columnFilters,
  });

  const fetchRequestsDocuments = async () => {
    setLoading(true);
    const offset = currentPage - 1;
    const filterParams = {};

    columnFilters.forEach((filterItem) => {
      filterParams[filterItem.id] = filterItem.value;
    });

    const filtersChanged =
      JSON.stringify(prevFilterParamsRef.current) !==
      JSON.stringify(filterParams);

    // If filters changed, reset page to 1
    if (filtersChanged) {
      setCurrentPage(1);
    }

    prevFilterParamsRef.current = filterParams;

    const sortParam = sorting
      .map((sortItem) => {
        return `${sortItem.id}=${sortItem.desc ? "desc" : "asc"}`;
      })
      .join(",");

    const params = {
      isAdmin: 1,
      userId: 1,
      limit: pageSize,
      offset: offset,
      ...filterParams,
      global_search: debouncedGlobalFilter,
      sort: sortParam,
      ...(createdAt ? { client_created_at: convertToUTC(createdAt) } : {}),
      ...(lastRequestedDate
        ? { last_request_date: convertToUTC(lastRequestedDate) }
        : {}),
    };
    const queryString = new URLSearchParams(params).toString();
    const url = `${baseUrl}/api/get_document_request_reports?${queryString}`;

    const response = await requests({ token, api: url });
    if (response.code == 200) {
      setRequestsDocumentsResponse(response?.data);
      setTotalPages(Math.ceil(response?.document_report_count / pageSize));
    } else {
      setRequestsDocumentsResponse([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const currentState = {
      createdAt,
      lastRequestedDate,
      debouncedGlobalFilter,
      columnFilters,
    };

    // If filters or sorting change, reset page to 1
    if (JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState)) {
      if (currentPage !== 1) {
        setCurrentPage(1);
        return; // Avoid double API call
      }
    }

    prevStateRef.current = currentState;
    fetchRequestsDocuments();
  }, [
    api,
    debouncedGlobalFilter,
    currentPage,
    columnFilters,
    pageSize,
    sorting,
    createdAt,
    lastRequestedDate,
  ]);

  useEffect(() => {
    setApi(
      `${baseUrl}/api/get-doc-requested-data-of-client?limit=${paginationModel.pageSize}&offset=${paginationModel.page}&userId=${user_id}&isAdmin=${is_admin}`
    );
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleFilterModelChange = (newModel) => {
    const queryParams = newModel.items
      .map((item) => {
        return `${item.field}=${item.value}`;
      })
      .concat(
        newModel.quickFilterValues.map((value) => `global_search=${value}`)
      )
      .join("&");

    const newApi = `${baseUrl}/api/get-doc-requested-data-of-client?limit=${paginationModel.pageSize}&offset=${paginationModel.page}&userId=${user_id}&isAdmin=${is_admin}&${queryParams}`;

    setApi(newApi);
  };

  const columns = [
    {
      accessorKey: "name",
      displayName: "Client Name",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Client Name
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="wrap-text limited-width capitalize text-left underline cursor-pointer text-blue-500">
          <a
            href={`${sugarCasesUrl}/${row.original.contact_id}`}
            target="_blank"
          >
            {row.getValue("name")}
          </a>
        </div>
      ),
    },
    {
      accessorKey: "client_created_at",
      displayName: "Client Created At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Client Created At
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const clientCreatedAt = row.getValue("client_created_at");
        return (
          <div className="capitalize text-wrap">
            {formatDate(clientCreatedAt)}
          </div>
        );
      },
    },
    {
      accessorKey: "total_requested",
      displayName: "Total Requested",
      header: ({ column }) => (
        <Button variant="ghost" className="p-0 !cursor-default">
          Total Requested
        </Button>
      ),
      cell: ({ row }) => {
        return (
          <div className="capitalize text-wrap">
            {row.getValue("total_requested")}
          </div>
        );
      },
    },
    {
      accessorKey: "last_requested_date",
      displayName: "Last Requested Date",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Last Requested Date
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const lastRequestedDate = row.getValue("last_requested_date");
        return (
          <div className="capitalize text-wrap">
            {lastRequestedDate ? formatDate(lastRequestedDate) : ""}
          </div>
        );
      },
    },
    {
      accessorKey: "total_uploaded",
      displayName: "Total Uploaded",
      header: ({ column }) => (
        <Button variant="ghost" className="p-0 !cursor-default">
          Total Uploaded
        </Button>
      ),
      cell: ({ row }) => {
        return (
          <div className="capitalize text-wrap">
            {row.getValue("total_uploaded")}
          </div>
        );
      },
    },
    {
      accessorKey: "total_approved",
      displayName: "Total Approved",
      filterable: false,
      header: ({ column }) => (
        <Button variant="ghost" className="p-0 !cursor-default">
          Total Approved
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("total_approved")}</div>
      ),
    },
    {
      accessorKey: "processed_by_user",
      displayName: "Processed by User",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Processed by User
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="w-full flex items-center justify-start wrap-text limited-width">
          {row.getValue("processed_by_user")}
        </div>
      ),
    },
    {
      accessorKey: "processed_date",
      displayName: "Processed Date",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Processed Date
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const processedDate = row.getValue("processed_date");
        return (
          <div className="capitalize text-wrap">
            {processedDate ? formatDate(processedDate) : ""}
          </div>
        );
      },
    },
    {
      accessorKey: "total_rejected",
      displayName: "Total Rejected",
      filterable: false,
      header: ({ column }) => (
        <Button variant="ghost" className="p-0 !cursor-default">
          Total Rejected
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("total_rejected")}</div>
      ),
    },
  ];

  const table = useReactTable({
    data: requestsDocumentsResponse,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pageSize: 20,
    },
    manualPagination: true,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pageSize,
    },

    globalFilterFn: (rows, columnFilters) => {
      const [globalFilter] = columnFilters.filter(
        (filter) => filter.id === "global"
      );
      if (!globalFilter || !globalFilter.value) return rows;

      return rows.filter((row) => {
        const name = row.original.name.toLowerCase(); // Assuming 'name' is the column key
        return name.includes(globalFilter.value.toLowerCase());
      });
    },
    onGlobalFilterChange: (filterValue) => {
      setGlobalFilter(filterValue); // Update the global filter state
    },
  });

  return (
    <div className="starred-container overflow-auto">
      <div className="navbar">
        <Navbar />
      </div>
      <div className="starred-content">
        <div className="starred-table-content">
          <ReportsTable
            requestsDocumentsResponse={requestsDocumentsResponse}
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            columns={columns}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            totalPages={totalPages}
            loading={loading}
            setPageSize={setPageSize}
            createdAt={createdAt}
            updatedAt={lastRequestedDate}
            setCreatedAt={setCreatedAt}
            setUpdatedAt={setLastRequestedDate}
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentsReports;
